import { LEGACY_ROUTES, resolveTarget } from './redirects'
import { ALL_SCREENS, SCREENS, activeTabFor } from './screens'
import type { Screen } from './screens'
import type { FigureSpec } from './figures'
import type { TutorialId } from './tutorials'

/**
 * What each screen and each tab is for, and the function that decides which one a URL is asking
 * about.
 *
 * This console explains itself unusually well and had nowhere to put the one explanation nobody had
 * written: *what am I looking at, and how do I do the thing I came here to do.* Everything already
 * written answers a question an operator knows how to ask. `Explainer` folds a screen's long-form
 * working away on six screens. `emptyState.ts` says what an empty list means. `CONSOLE.md` is a
 * thousand lines of why each screen is shaped the way it is, and it is in the repository rather than
 * in the product.
 *
 * ## The rule this obeys, which is not the rule it looks like it breaks
 *
 * `CONSOLE.md` states a split under *Where the long explanations went*: **an explanation somebody
 * reads once folds away, and a warning somebody decides against acting on stays on the page**, and a
 * disclosure earns its place only when an explanation has nowhere better to live (D-432, D-423).
 *
 * This is not an exception to that and must not become one. Three constraints, and the first two are
 * enforced rather than intended:
 *
 * 1. **Nothing moved in here.** Every `Explainer`, `lede`, `hint` and `role="note"` warning says
 *    exactly what it said before. Trust's rotation warning is still readable with nothing opened, and
 *    `devices.spec.ts` asserts it.
 * 2. **No entry carries a reason not to press a button.** Help answers *what is this* and *how do I*.
 *    A warning that changes whether somebody acts belongs on the page, because a warning only seen
 *    after asking is a warning too late. A `caveat` here corrects a *misreading* - "Machines counts
 *    arrivals, not machines running now" - and never withholds a consequence.
 * 3. **An entry is small, and `help.test.ts` makes that mechanical**: one to five points, a hundred
 *    and forty characters each. A panel that cannot grow cannot become the manual D-432 moved off the
 *    page. Where a shape says it faster than a sentence - how a dictionary reaches a device, what a
 *    kit holds - the entry names a figure rather than spending a point on it.
 *
 * What is left is the thing none of those can carry: the shape of the job a screen is one step of.
 * "A dictionary does nothing until a policy binds it" belongs to neither the Dictionaries tab nor the
 * policy editor, which is exactly the nowhere-better-to-live test the existing rule already states.
 * Where an explanation does have a home, an entry points at it instead of repeating it.
 *
 * ## Why this is a table and not a registry
 *
 * `ShortcutProvider` and `SavedViewsProvider` sit two files away and both work the other way round:
 * a screen publishes what it can offer, because the shell must not know which screens have an
 * inspector. The shell *can* know this, because the answer is a function of the URL - so a registry
 * would mean twenty-eight screens each remembering to register, every one of which can forget. That
 * is the state the views pane was in before D-450, printing a hint on eleven routes that could not
 * honor it. Copy as data, for the reason `emptyState.ts` gives: it is something a reviewer can read
 * in one place, and one screen cannot say two different things in two different branches.
 */

/**
 * Every key this table must have, derived from the inventory rather than written out.
 *
 * A screen with tabs contributes one key per tab and none for itself; a screen without contributes
 * one. So adding a tab to `screens.ts` makes `HELP` fail `tsc --noEmit` until somebody writes the
 * copy - which is the same reason `lib/icons.ts` gives for `IconName` being a union rather than
 * `string`, and it is a better gate than a test because it fires at the keyboard.
 *
 * `help.test.ts` carries a `@ts-expect-error` canary on a key that does not exist. Without it, an
 * edit that dropped `as const` from `SCREENS` would widen this to `string`, the `Record` below would
 * stop gating anything, and nothing would say so.
 */
type KeysOfScreen<S> = S extends { route: infer R extends string; tabs: readonly (infer T)[] }
  ? T extends { key: infer K extends string }
    ? `${R}#${K}`
    : never
  : S extends { route: infer R extends string }
    ? R
    : never

export type HelpKey = KeysOfScreen<(typeof SCREENS)[number]>

export interface HelpEntry {
  /** Names the tab as well as the screen: "Devices, Enrollment". Shown as the dialog's title. */
  title: string
  /** What this screen is for. One sentence, and the one thing to read if nothing else is. */
  lead: string
  /**
   * The two to five things an operator actually asks here.
   *
   * A budget rather than a style note, and asserted. Five is what somebody reads standing up.
   */
  points: readonly string[]
  /**
   * The one thing about this screen that is most often misread, where there is one.
   *
   * A correction, never a consequence. See constraint 2 in this file's own comment: if a sentence
   * would change whether somebody presses a button, it belongs on the page and not behind a click.
   */
  caveat?: string
  /** Walkthroughs worth reading from here, in the order they are worth reading. */
  tutorials?: readonly TutorialId[]
  /** A drawing of the job this screen is one step of, drawn under the lead. */
  figure?: FigureSpec
}

/**
 * What the panel shows for a URL this table does not know.
 *
 * A declared route reaching this is a `help.test.ts` failure, so in a shipped build it is only
 * reachable by typing a path that does not exist. It says so plainly and offers the walkthroughs,
 * for the reason `tabFor` gives for having a fallback at all: a blank pane with working navigation
 * around it reads as a broken deployment rather than as a screen with nothing written for it.
 */
export const UNKNOWN_HELP: HelpEntry = {
  title: 'About this console',
  lead: 'There is no help written for this address, which usually means the address is wrong.',
  points: [
    'The five destinations down the left are the screens used daily. Everything else is under your name in the top bar.',
    'Every screen and every tab has its own help. Press this button again once you are on one.'
  ],
  tutorials: ['dictionary', 'policy', 'binding', 'deploy']
}

export const HELP: Record<HelpKey, HelpEntry> = {
  '/board': {
    title: 'Board',
    lead: 'The state of the fleet at a glance, arranged the way you want it.',
    points: [
      'Every panel is yours rather than the organization’s. Rearranging it changes nothing for anybody else.',
      'Customise adds, removes, reorders and resizes panels. Settings has the same controls.',
      'A panel that could not read its data says so rather than drawing a zero.'
    ],
    caveat:
      'A number that was not measured is never shown as one. An empty panel means the read failed, not that the fleet is quiet.'
  },
  '/cases#cases': {
    title: 'Cases',
    lead: 'One row per thing worth deciding about, folded from however many times it happened.',
    points: [
      'Occurrences sharing a device, an assistant, a rule set and a quarter of an hour are one case with a count.',
      'Open a case to see its occurrences, its evidence and who has touched it.',
      'Acknowledging says somebody is looking. Resolving says it is dealt with, and asks why.',
      'Suppressing stops a rule raising this case again, and says for how long.'
    ],
    tutorials: ['policy'],
    figure: 'case-folding'
  },
  '/cases#raw': {
    title: 'Cases, Raw detections',
    lead: 'Every detection the fleet reported, unfolded, newest first.',
    points: [
      'The same events a case is made of, before they were grouped.',
      'Open one to read what the agent sent, byte for byte, and what was made of it.',
      'Prompt text is read from the stored body on demand and never from the normalized record.'
    ],
    caveat:
      'Every read of a prompt is recorded against your name. That is a promise made to the organization, not a restriction on you.'
  },
  '/cases#query': {
    title: 'Cases, Query',
    lead: 'Ask the event store a question directly, and save the ones worth asking again.',
    points: [
      'The expression is checked as you type, and says where it is wrong rather than only that it is.',
      'Examples are there to be taken apart. Start from one rather than from an empty box.',
      'A result exports as CSV. A saved query becomes a view in the pane on the left.'
    ]
  },
  '/cases/:alertId': {
    title: 'A case',
    lead: 'Everything known about one case, and the two decisions you can make about it.',
    points: [
      'The timeline is every occurrence folded into this case, in the order they arrived.',
      'Evidence is whatever the agent uploaded, including a capture if a policy asked for one.',
      'Stop this happening again drafts a policy from what this case matched. It is a draft; nothing reaches a device.'
    ],
    tutorials: ['policy']
  },
  '/people': {
    title: 'People',
    lead: 'Exposure by person rather than by rule, which is the question a director asks.',
    points: [
      'Who is sending company data out, ranked, over the window you choose.',
      'The score is arithmetic and the working is on the page. Open the formula and check it.',
      'A person here is an account an agent read off a signed-in assistant, not a directory entry.'
    ],
    caveat:
      'Somebody absent from this list is somebody no policy observed, which is not the same as somebody who sent nothing.'
  },
  '/policies#policies': {
    title: 'Policies',
    lead: 'What the fleet runs: one policy says when to do something, under what condition, and what to do with the result.',
    points: [
      'New policy asks three questions: what to watch, where it runs, and what to do about it.',
      'Policies built together are listed together under one name, and open as one group.',
      'A banner says how many drafts are waiting for a person. A draft reaches no device.',
      'Removing policies happens here, in bulk, because that is a list problem.'
    ],
    tutorials: ['policy', 'binding'],
    figure: { id: 'flow', focus: [2] }
  },
  '/policies#dictionaries': {
    title: 'Policies, Dictionaries',
    lead: 'The words the fleet looks for, kept in named sets that a policy binds by reference.',
    points: [
      'Eight collections ship with every organization. They are ordinary dictionaries and you may edit them.',
      'A policy binds a dictionary rather than copying it, so correcting a term reaches the fleet by republishing.',
      'Publishing a dictionary freezes that revision. The next edit starts the next one.',
      'Dictionaries are retired rather than deleted, and retiring one a policy binds is refused by name.'
    ],
    caveat:
      'A dictionary on its own does nothing. It reaches a machine only through a policy that binds it and is then published.',
    tutorials: ['dictionary', 'binding'],
    figure: { id: 'flow', focus: [1] }
  },
  '/policies#messages': {
    title: 'Policies, Messages',
    lead: 'What a policy tells the person at the keyboard when it stops or flags a prompt, in the words of your own handbook.',
    points: [
      'Give a message the handbook section it cites, and it is filled in wherever the words say {reference}.',
      'A policy chooses one under "When a rule matches". The builder asks as its last step, and a group chooses once for all its policies.',
      'The built-in messages cannot be changed. Copy one to make it yours.',
      'Retiring a message a policy uses is refused by name, because that policy could no longer be published.'
    ],
    caveat:
      'Changing a message changes no device by itself. A policy carries the words it was last published with, so publish the policies that use it again.',
    tutorials: ['policy']
  },
  '/policies#assistant': {
    title: 'Policies, Assistant',
    lead: 'Drafts a policy or a dictionary through a model, and can never approve or publish anything.',
    points: [
      'Every draft opens in the ordinary editor, where a person reviews it and saves it.',
      'Prompts leave the building, redacted, and the audit below shows exactly what was sent.',
      'The term review is arithmetic rather than a model, so it still answers when the provider is down.'
    ],
    tutorials: ['dictionary', 'policy']
  },
  '/policies/build': {
    title: 'Build a policy',
    lead: 'Three questions: what to watch, where it runs, and what should happen.',
    points: [
      'Pick an AI assistant, or the whole computer for an inventory or a daily screenshot.',
      'Tick every place the assistant runs that you want covered. Each becomes its own policy, kept together as one group.',
      'A choice that cannot work where you are looking is greyed out, and says why.',
      'Create makes drafts. Nothing reaches a device until the group is approved and published.'
    ],
    caveat:
      'Watching an assistant needs prompt observation, which agents built for release do not include yet. The page says so above the choices.',
    tutorials: ['policy', 'binding'],
    figure: 'policy-builder'
  },
  '/policies/groups/:groupId': {
    title: 'A policy group',
    lead: 'The policies one builder choice became, approved, published and retired together.',
    points: [
      'One row per place the assistant runs, and one per screenshot if you asked for them.',
      'Approve all is all or nothing. Publish all checks every policy compiles before it sends any.',
      'Advanced settings opens one policy in the full editor. A policy edited there is marked, and publishing the group includes the edit.',
      'Retire group stops editing; stopping devices running them is a separate box, ticked by default.'
    ],
    tutorials: ['policy'],
    figure: 'save-approve-publish'
  },
  '/policies/new': {
    title: 'A new policy',
    lead: 'The full editor, for building a policy by hand: what it is, what it does, what it looks for, when it runs, and what happens with the result.',
    points: [
      'The builder on New policy is quicker for anything it offers. This is for the rest.',
      'Take a template if one fits. One that acts on a subscription plan is offered only where that plan has actually been read.',
      'The form never checks your work itself. Every finding comes from the server, which is the same validator the save path uses.',
      'Section 3 appears only for a policy that watches prompts.',
      'Save, then Approve, then Publish. Publishing assigns to every enrolled device and asks first.'
    ],
    tutorials: ['policy', 'binding'],
    figure: 'policy-anatomy'
  },
  '/policies/:policyId': {
    title: 'The policy editor',
    lead: 'The same five sections, now against a policy that exists and has revisions.',
    points: [
      'The form edits the parameters in place rather than rebuilding them, so a setting it does not show still survives an edit.',
      'JSON is the advanced view of the same document. Switching back needs it to parse.',
      'Publish is refused until the revision is approved, and reads back how many devices run the one it replaces.',
      'Section 5 decides whether results are published at all, and under what condition.'
    ],
    caveat:
      'Binding a dictionary saves at once and deliberately does not publish. What a policy carries and when the fleet gets it are two decisions.',
    tutorials: ['binding', 'policy'],
    figure: 'save-approve-publish'
  },
  '/dictionaries/:dictionaryId': {
    title: 'A dictionary',
    lead: 'The rules in one dictionary, and what they would match.',
    points: [
      'Add a rule from above the table; edit one from the button on its own row. Both use the same dialog.',
      'Saving a rule whose name already exists replaces it. That is what makes an edit possible, and renaming one writes a second rule.',
      'Show what would match runs your terms against sample text before anybody depends on them.',
      'Publish freezes this revision, then republish the policies that bind it.'
    ],
    caveat:
      'Publishing here reaches no device by itself. The policies that bind this dictionary have to be published again.',
    tutorials: ['dictionary', 'binding'],
    figure: 'revision-publish'
  },
  '/devices#devices': {
    title: 'Devices, Fleet',
    lead: 'Every machine running the agent, and whether it is doing what you think.',
    points: [
      'A device that is checking in is not necessarily running anything. The columns say which.',
      'Open a row for what one machine has and has not told the server.',
      'What these columns mean is on the page, folded, under the table.'
    ],
    caveat:
      'A machine appears here within a minute of its first check-in, and not when somebody installs the agent.',
    tutorials: ['deploy']
  },
  '/devices#deployment': {
    title: 'Devices, Enrollment',
    lead: 'Every way into this organization, as one list: kits, and the bare tokens without an installer around them.',
    points: [
      'Building a kit issues exactly one token, so a kit and its token are one row.',
      'A kit downloads once and is never stored. Build another like this is how a team is given one again.',
      'The counts beside the filters are the whole organization, not this page.',
      'Revoking withdraws a way in and cannot be undone. Machines that already joined keep working.'
    ],
    caveat:
      'Machines counts arrivals rather than machines running now. A reimaged machine returns to its own row and costs neither a seat nor a use.',
    tutorials: ['deploy'],
    figure: 'kit-contents'
  },
  '/devices#checkin': {
    title: 'Devices, Check-in',
    lead: 'How often the fleet polls this server, and how long the credential it polls with lasts.',
    points: [
      'Two numbers that multiply into one answer: how long a machine can be switched off and still come back.',
      'Slowing the fleet down asks first, because it is the change that strands a machine.',
      'A credential rotates only at a successful check-in, which is why the cadence decides the rest.'
    ],
    tutorials: ['deploy'],
    figure: 'checkin-window'
  },
  '/devices#trust': {
    title: 'Devices, Trust',
    lead: 'The signing key this organization’s policies are signed with, and what every enrolled device has pinned.',
    points: [
      'A device pins the anchor it enrolled against and will refuse a policy signed by anything else.',
      'Rotating issues a new anchor. The warning above says what that costs, and it is on the page on purpose.',
      'What pinning means is folded under the card.'
    ],
    figure: 'trust-anchor'
  },
  '/devices/:deviceId': {
    title: 'A device',
    lead: 'One machine: what it is running, when it last spoke, and what it has reported.',
    points: [
      'Policies listed here are what this device was served, which is not always what it applied.',
      'Retiring a device stops it being counted and does not reach the machine.',
      'What this page can and cannot tell you is folded near the bottom, and worth reading once.'
    ]
  },
  '/events/:eventRecordId': {
    title: 'A detection',
    lead: 'One event, as the agent sent it and as the server made sense of it.',
    points: [
      'The stored body is byte for byte what arrived. The normalized record is a projection of it.',
      'Prompt text is read from that body on demand, and the read is recorded against your name.',
      'The account panel says which plan was resolved, or says plainly that none was.'
    ],
    caveat:
      'A plan the agent did not resolve is never reported as one. Unresolved says unresolved rather than showing the free tier.'
  },
  '/system': {
    title: 'System',
    lead: 'How long this organization keeps what it holds, and what has gone wrong lately.',
    points: [
      'Retention is set per organization, separately for events, prompt text and artifacts.',
      'Dead letters are messages that could not be processed. They are kept rather than dropped.',
      'A projection can be rebuilt from the events it was derived from.'
    ],
    caveat:
      'The stored device bodies are never swept. The prompt sweep rewrites three named fields inside an aged body and deletes nothing.'
  },
  '/export': {
    title: 'Export',
    lead: 'What this organization sends to a SIEM, and exactly what those bytes look like.',
    points: [
      'The preview is first because it is what this screen is worth opening for before anything is configured.',
      'A destination is added from the destinations card. Removing one names the deliveries that go with it.',
      'Delivery is at least once, so a destination has to tolerate a repeat.'
    ],
    caveat:
      'Anything in the exported record reaches every destination. There is no per-field control, which is why prompt text is not in it.'
  },
  '/settings#appearance': {
    title: 'Settings, Appearance',
    lead: 'Which palette this console is painted in, for you.',
    points: [
      'Per account rather than per organization. Two people looking after one fleet need not agree about dark mode.',
      'Follow the system reads the machine you are sitting at.'
    ]
  },
  '/settings#password': {
    title: 'Settings, Your password',
    lead: 'Replace the password this account signs in with.',
    points: [
      'The rules are set by your organization, and the form says which ones a candidate fails.',
      'Changing it does not end your other sessions.'
    ]
  },
  '/settings#dashboard': {
    title: 'Settings, Dashboard',
    lead: 'Which panels the Board shows, in what order, drawn which way.',
    points: [
      'The same controls the Board offers under Customise, in one place.',
      'A panel this build no longer has is dropped from a saved layout rather than guessed at.'
    ]
  },
  '/settings#provider': {
    title: 'Settings, Provider',
    lead: 'The credential this deployment uses to reach the model provider the assistant runs on.',
    points: [
      'Owner only, and the key is never returned once stored.',
      'The screen says where the key in use came from, which is how you tell a stored key from a configured one.'
    ]
  },
  '/accounts': {
    title: 'Accounts',
    lead: 'Who can sign in to this organization, what each of them may do, and every time somebody running the deployment opened it.',
    points: [
      'You type the first password, so it is a secret two people share until it is replaced.',
      'A new account is held at a change-password screen before it can reach anything.',
      'The password policy below applies to everybody here, including you.',
      'Platform support can unlock, reset or re-enable somebody here, but cannot add people or change roles.'
    ],
    caveat:
      'The support-access record is what somebody running this deployment did inside your organization. It is kept whether or not anybody reads it.'
  },
  '/branding': {
    title: 'Branding',
    lead: 'What this organization looks like: its color, its mark, and the host its people sign in on.',
    points: [
      'The accent is checked for legibility against every surface it will be painted on.',
      'The sign-in page can carry the mark and the footer line, read without anybody being signed in.'
    ],
    caveat:
      'The unbranded read deliberately never returns the organization’s name. The sign-in form must not become a way of discovering which organizations exist.'
  },
  '/platform/tenants': {
    title: 'Organizations',
    lead: 'Every organization on this deployment, and the way into one when a customer asks for help.',
    points: [
      'Opening one starts a support session that is recorded, time limited and visible to them throughout.',
      'A new organization needs a signing key before it can be served a policy, and the banner says whether it got one.',
      'Retiring an organization stops it being used. Deleting one is a separate act and destroys what it owns.'
    ]
  },
  '/platform/administrators': {
    title: 'Administrators',
    lead: 'Who runs this deployment, rather than who runs an organization on it.',
    points: [
      'These accounts can open any organization. That is the whole of what makes them different.',
      'Resetting one hands over a password that has to be replaced on first use.'
    ]
  }
}

/**
 * Whether two segment lists match, treating a `:param` segment as a wildcard.
 *
 * Segment by segment rather than by regular expression, because a route is already a list of
 * segments and turning it into a pattern and back is a step that can be got wrong.
 */
function segmentsMatch(pattern: readonly string[], actual: readonly string[]): boolean {
  if (pattern.length !== actual.length) return false
  return pattern.every(
    (segment, index) => segment.startsWith(':') ? actual[index]!.length > 0 : segment === actual[index]
  )
}

function segmentsOf(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0)
}

/** How many segments of a route are literal, so the most specific candidate can be preferred. */
function literalCount(route: string): number {
  return segmentsOf(route).filter((segment) => !segment.startsWith(':')).length
}

function screenForPath(path: string): Screen | null {
  // A literal route wins outright, which is what makes `/policies/new` beat `/policies/:policyId`
  // with no ordering rule for anybody to remember. It is the same longest-literal-wins doctrine
  // `groupFor` uses for the navigation.
  const exact = ALL_SCREENS.find((screen) => screen.route === path)
  if (exact) return exact

  const actual = segmentsOf(path)
  const candidates = ALL_SCREENS.filter((screen) => segmentsMatch(segmentsOf(screen.route), actual))
    // Defence in depth. The exact match above already decides the only ambiguous pair this console
    // has; sorting means the order still holds if somebody later reads that lookup as redundant.
    .sort((left, right) => literalCount(right.route) - literalCount(left.route))

  return candidates[0] ?? null
}

/**
 * The key a URL is asking for, or null when this console has nothing written for it.
 *
 * Pure, and takes three primitives rather than a `Location`, so the whole resolution table is an
 * `it.each` with no DOM, no router and no component test - the same reason `lib/shortcuts.ts` is
 * written against event-shaped objects.
 *
 * Roles are a parameter because the answer depends on them, once. `?tab=provider` selects the
 * Provider tab for an owner and Appearance for everybody else, because `SettingsPage` filters before
 * it resolves. A role-blind resolver would describe a tab nobody is looking at.
 */
export function helpKeyFor(pathname: string, search: string, roles: readonly string[]): HelpKey | null {
  // Defensive: react-router hands these separately, but this is also called from tests and from
  // `tutorials.test.ts` with whole URLs.
  const withoutQuery = pathname.split('?')[0]!.split('#')[0]!
  const path = withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : withoutQuery

  /*
    A legacy path resolves through the table that already redirects it, so every URL this console
    answers has help - including a bookmarked `/trust` during the frame the redirect is on screen.

    Two subtleties. `from !== to` is the same guard `STATIC_REDIRECTS` and `PARAMETERISED_REDIRECTS`
    apply, and here it is load-bearing rather than tidy: `/events/:eventRecordId` redirects to itself,
    so without it this would resolve that row to itself and recurse until the stack ran out. And the
    target is resolved with no parameters on purpose - `resolveTarget` leaves an unmatched `:name`
    in place, which is exactly the spelling a screen route is declared with, so `/alerts/xyz` becomes
    `/cases/:alertId` and matches the screen directly.
  */
  const legacy = LEGACY_ROUTES.find(
    (route) => route.from !== route.to && segmentsMatch(segmentsOf(route.from), segmentsOf(path))
  )

  if (legacy) {
    const [targetPath, targetSearch] = resolveTarget(legacy.to, {}).split('?')
    return helpKeyFor(targetPath!, targetSearch ? `?${targetSearch}` : '', roles)
  }

  const screen = screenForPath(path)
  if (!screen) return null

  const tab = activeTabFor(screen, search, roles)
  const key = (tab === null ? screen.route : `${screen.route}#${tab}`) as HelpKey

  return key in HELP ? key : null
}

/** The entry the panel shows. Never undefined, for the reason `UNKNOWN_HELP` gives. */
export function helpFor(pathname: string, search: string, roles: readonly string[]): HelpEntry {
  const key = helpKeyFor(pathname, search, roles)
  return key === null ? UNKNOWN_HELP : HELP[key]
}

/** Every key a screen contributes, for the completeness gate and for enumerating the table. */
export function keysOf(screen: Screen): readonly string[] {
  if (!screen.tabs || screen.tabs.length === 0) return [screen.route]
  return screen.tabs.map((tab) => `${screen.route}#${tab.key}`)
}
